import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Form, Input, Select, Alert, InputNumber, Checkbox } from "antd";
const { TextArea } = Input;
import "./index.scss";
import More, { MoreRef } from "../More";
import { useTranslation } from "react-i18next";
import { debounce } from "@/pages/Personal/util";
import { TitleList } from "../../../api/personal";
import { fetchAttorney } from "../../../api/common";
import { IS_MOBILE, calculateZhChar } from "@/utils/tool";
import sentry4Action from "@/utils/sentry";
import LoginModal from "../Login";
import useClickOutside from "@/hooks/useClickOutside";
import { clsx } from "clsx";
import {
  TitleType,
  SOURCE_ORDER_TYPE,
  OrderTypeToInvoiceContentMap,
} from "@/utils/contants";

export interface InvoiceModalProps {
  hiddenModal: (isCancel?: boolean) => void;
  invoiceInfo?: InvoiceInfo & InvoiceAddress;
  getInvoiceInfo?: (info: InvoiceInfo & InvoiceAddress) => void;
  additionalParams?: {
    money?: number;
    orderIds?: Array<string>;
    orderType: OrderType;
  };
  submitAction?: (info: InvoiceInfo & InvoiceAddress) => Promise<void>;
  orderIds: Array<string>;
}

interface IAttorney {
  power_of_attorney: string;
  publishing_house_name: string;
}

const Invoice: React.FC<InvoiceModalProps> = ({
  hiddenModal,
  getInvoiceInfo,
  invoiceInfo,
  additionalParams,
  submitAction,
  orderIds,
}) => {
  const [form] = Form.useForm();
  const billType = Form.useWatch("bill_type", form);
  const titleType = Form.useWatch("title_type", form);
  const { t, i18n } = useTranslation();
  const [isLogin, setIsLogin] = useState(false);
  const [titleList, setTitleList] = useState<
    Array<TitlePerson> | Array<TitleEnterprise>
  >([]);
  const [titleListShow, setTitleListShow] = useState(false);
  const [historyTitleShow, setHistoryTitleShow] = useState(false);
  const [modalInstance, setModalInstance] = useState(false);
  const [attorney, setAttorney] = useState<Array<IAttorney>>([]);
  const [attorneyModal, setAttorneyModal] = useState(false);
  const titleInputRef = useRef(null);
  const moreRef = useRef<MoreRef>(null);

  useClickOutside(titleInputRef, () => {
    setTitleListShow(false);
  });

  const billContentOptions = useMemo(() => {
    if (!additionalParams) return [];
    switch (additionalParams.orderType) {
      case SOURCE_ORDER_TYPE.Pod:
        return [{ value: "期刊", label: "印刷品/期刊" }];
      case SOURCE_ORDER_TYPE.Apc:
      case SOURCE_ORDER_TYPE.Ptp:
        return [
          { value: "版面费", label: "版面费" },
          { value: "文章处理费/APC", label: "文章处理费/APC" },
          { value: "出版服务费", label: "出版服务费" },
        ];
      case SOURCE_ORDER_TYPE.Meeting:
        return [
          { value: "会议注册费", label: "会议注册费" },
          { value: "会议服务", label: "会议服务" },
        ];
      case SOURCE_ORDER_TYPE.Ppv:
        return [{ value: "信息服务费", label: "信息服务费" }];
      default:
        return [];
    }
  }, [additionalParams?.orderType]);

  useEffect(() => {
    const userInfo = localStorage.getItem("user");
    if (userInfo) {
      setIsLogin(true);
    }
    const getAttorneyInfo = async (order_ids) => {
      const res = await fetchAttorney({ order_ids });
      if (res?.data) {
        setAttorney(res.data as Array<IAttorney>);
      }
    };
    getAttorneyInfo(orderIds);
  }, []);

  const handleSubmit = () => {
    sentry4Action("支付页面-提交按钮", {});

    form
      .validateFields()
      .then((values) => {
        if (submitAction) {
          submitAction(values);
        } else {
          getInvoiceInfo(values);
        }
        hiddenModal();
      })
      .catch((info) => {
        // 如果moreRef存在，且错误信息中有title_bank_name, title_bank_card_no, title_address, title_telephone中的任意一个，则打开moreRef
        if (moreRef.current) {
          const errorFields = info.errorFields.map((item) => item.name[0]);
          const moreFields = [
            "title_bank_name",
            "title_bank_card_no",
            "title_address",
            "title_telephone",
          ];
          if (errorFields.some((item) => moreFields.includes(item))) {
            moreRef.current.setOpen(true);
          }
        }
      });
  };

  const titleChange = async (e) => {
    const { value } = e.target;

    form.setFieldValue("title_title", value);
    if (value) {
      const finalEnterprise = titleList.filter((item) =>
        item.title.includes(value),
      );
      setTitleList(finalEnterprise);
      setTitleListShow(true);
    }
  };

  const debouncedTitleChange = debounce(titleChange, 100);

  const chooseTitle = (item) => {
    setTitleListShow(false);
    form.setFieldsValue({ title_title: item.title });
    if (titleType === 3 || titleType === TitleType.Company) {
      form.setFieldsValue({
        title_taxpayer_identification_number:
          item.taxpayer_identification_number,
        title_bank_name: item.bank_name,
        title_bank_card_no: item.bank_card_no,
        title_address: item.address,
        title_telephone: item.telephone,
      });
      return;
    }
  };

  const submitAction4Login = () => {
    const userInfo = localStorage.getItem("user");
    if (userInfo) {
      setIsLogin(true);
    }
  };

  const handleInputFocus = async (e) => {
    const { value } = e.target;

    if (!value.trim()) {
      if (isLogin) {
        const res = await TitleList<{
          enterprise: Array<TitleEnterprise>;
          government: Array<TitleEnterprise>;
          person: Array<TitlePerson>;
        }>();
        if (res.code === 0) {
          console.log(`handleInputFocus-TitleList`);
          const { enterprise, person, government } = res.data;

          if (titleType === TitleType.Company) {
            // const finalEnterprise = enterprise.filter(item=>item.title.includes(value))
            setTitleList(enterprise);
          } else if (titleType === TitleType.Government) {
            setTitleList(government);
          } else {
            // const finalPerson = enterprise.filter(item=>item.title.includes(value))
            setTitleList(person);
          }
          setTitleListShow(true);
        }
      } else {
        setHistoryTitleShow(true);
      }
    }
  };

  const handleInputBlur = () => {
    if (historyTitleShow) {
      setTimeout(() => {
        setHistoryTitleShow(false);
      }, 1000);
    }
  };

  const downloadPowerOfAttorney = async (url: string) => {
    window.open(url, "_blank", "noopener, noreferrer");
  };
  /**
   * 发票类型为专用发票时，抬头类型只能为企业
   * @param value
   */
  const handleBillTypeChange = (value) => {
    form.setFieldsValue({
      title_title: "",
      title_taxpayer_identification_number: "",
      title_enterprise_name: "",
      title_bank_name: "",
      title_bank_card_no: "",
      title_address: "",
      title_telephone: "",
    });
    if (value === 4) {
      form.setFieldValue("title_type", 1);
    }
  };
  const handleTitleTypeChange = (value) => {
    // console.log(value, `value`);
    //   1: 企业
    //   2: 个人
    form.setFieldsValue({
      title_title: "",
      title_taxpayer_identification_number: "",
      title_enterprise_name: "",
      title_bank_name: "",
      title_bank_card_no: "",
      title_address: "",
      title_telephone: "",
    });
    // if (value == 4) {
    //   form.setFieldsValue({
    //     title_enterprise_name: "",
    //     title_taxpayer_identification_number: "",
    //     title_bank_name: "",
    //     title_bank_card_no: "",
    //     title_address: "",
    //     title_telephone: "",
    //   });
    // }
  };

  /**
   * 验证备注长度，中文以及中文字符算两个
   * @param rule
   * @param value
   * @param callback
   * @returns
   */
  const remarkValidator = (rule: any, value: string) => {
    if (!value) return Promise.resolve();
    const { count } = calculateZhChar(value);
    if (count + value.length > 200) {
      return Promise.reject(t("超出字符限制"));
    }
    return Promise.resolve();
  };

  return (
    <>
      <Modal
        open
        wrapClassName="modal-wrapper"
        onCancel={() => hiddenModal(true)}
        footer={
          <>
            <button
              className={clsx("cancel", IS_MOBILE && "mobile_cancel")}
              onClick={() => hiddenModal(true)}
            >
              {t("取消")}
            </button>
            <button
              className={clsx("done", IS_MOBILE && "mobile_done")}
              onClick={handleSubmit}
            >
              {t("提交")}
            </button>
          </>
        }
        width="600px"
        title={t("填写发票信息")}
        centered
        maskClosable={false}
      >
        <>
          <section className="invoice-tips">
            <Alert
              message={
                <span>
                  {additionalParams.orderType === SOURCE_ORDER_TYPE.Pod
                    ? t("发票将在纸本期刊发货后完成开具。")
                    : t("发票将在提交后的5个工作日内完成开具。")}
                  {t(
                    "一旦发票完成开具，我们将通过您提供的电子邮件地址发送通知。如果您有任何疑问，请随时通过 publink@cnpiec.com.cn 与我们联系!",
                  )}
                  {attorney.length !== 0 && (
                    <a
                      //   onClick={() => downloadPowerOfAttorney(attorney)}
                      onClick={() => setAttorneyModal(true)}
                      className="down_a"
                    >
                      {t("下载出版社授权书")}
                    </a>
                  )}
                </span>
              }
              type="info"
              showIcon
            />
          </section>
          <Form
            form={form}
            colon={false}
            labelCol={{
              span: i18n.language === "en" ? 7 : 5,
              style: { textWrap: "wrap" },
            }}
            wrapperCol={{ span: 19 }}
            style={{ width: "90%" }}
            className="invoice-form"
            labelAlign="left"
            initialValues={
              invoiceInfo || {
                bill_type: 3,
                title_type: 1,
                bill_content:
                  OrderTypeToInvoiceContentMap[additionalParams.orderType] ||
                  "",
                print_detail: false,
              }
            }
          >
            {additionalParams.money ? (
              <>
                <Form.Item
                  name="bill_money"
                  label={t("金额")}
                  initialValue={additionalParams.money}
                  rules={[
                    {
                      required: true,
                      message: `${t("待开票金额")} ${t("不能为空")}`,
                    },
                    {
                      pattern: new RegExp(
                        /^(?!0(\.0{1,2})?$)([1-9]\d*|0)(\.\d{1,2})?$/,
                      ),
                      message: t("输入的金额不正确"),
                    },
                  ]}
                >
                  <InputNumber addonBefore={<div>{t("待开票金额")}</div>} />
                </Form.Item>
                <Form.Item
                  name="order_ids"
                  style={{ display: "none" }}
                  initialValue={additionalParams.orderIds}
                >
                  <div></div>
                </Form.Item>
              </>
            ) : (
              <Form.Item
                name="enable"
                style={{ display: "none" }}
                initialValue={true}
              >
                <div></div>
              </Form.Item>
            )}
            <Form.Item name="bill_type" label={t("发票类型")}>
              <Select
                options={[
                  { value: 3, label: `${t("电子发票（普通发票）")}` },
                  { value: 4, label: `${t("电子发票（增值税专用发票）")}` },
                ]}
                onChange={handleBillTypeChange}
              />
            </Form.Item>
            <Form.Item
              label={t("抬头类型")}
              shouldUpdate={(
                prevValues: InvoiceInfo,
                currentValues: InvoiceInfo,
              ) => prevValues.bill_type !== currentValues.bill_type}
            >
              {({ getFieldValue }) => {
                return (
                  <Form.Item name="title_type" noStyle>
                    <Select
                      options={[
                        { value: 1, label: `${t("企业或单位")}` },
                        { value: 2, label: `${t("个人")}` },
                        { value: 3, label: `${t("事业单位/军队/政府机构")}` },
                      ]}
                      onChange={handleTitleTypeChange}
                      disabled={getFieldValue("bill_type") === 4}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>
            <Form.Item label={t("抬头名称")} style={{ position: "relative" }}>
              <div ref={titleInputRef}>
                <Form.Item
                  name="title_title"
                  rules={[
                    {
                      required: true,
                      message: `${t("抬头名称")} ${t("不能为空")}`,
                    },
                  ]}
                  noStyle
                  style={{ marginBottom: 0 }}
                >
                  <Input
                    placeholder={t("必填")}
                    maxLength={255}
                    onChange={debouncedTitleChange}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    autoComplete="off"
                  />
                </Form.Item>
                {!isLogin ? (
                  historyTitleShow ? (
                    <section
                      className="additional-wrapper"
                      style={{ height: 32, minHeight: 40 }}
                    >
                      <div
                        className="to-login"
                        onClick={() => setModalInstance(true)}
                      >
                        {t("使用历史抬头")}
                      </div>
                    </section>
                  ) : null
                ) : (
                  titleList.length !== 0 &&
                  titleListShow && (
                    <section className="additional-wrapper">
                      <section className="title-list-wrapper">
                        {titleList.map((item) => (
                          <div
                            className="title-item"
                            key={item.id}
                            onClick={() => chooseTitle(item)}
                          >
                            {item.title}
                          </div>
                        ))}
                      </section>
                    </section>
                  )
                )}
              </div>
            </Form.Item>

            {[TitleType.Company, TitleType.Government].includes(titleType) && (
              <>
                <Form.Item
                  name="title_taxpayer_identification_number"
                  label={t("纳税人识别号")}
                  className="identification_number"
                  rules={[
                    () => ({
                      validator(_, value = "") {
                        if (titleType !== TitleType.Company && !value) {
                          return Promise.resolve();
                        }

                        if (
                          (titleType === TitleType.Company && value.length) ===
                          0
                        ) {
                          return Promise.reject(
                            new Error(`${t("纳税人识别号")} ${t("不能为空")}`),
                          );
                        }
                        const validateSpace = /\s/.test(value);
                        const validateNumberAndChar = /^[A-Z\d]+$/.test(value);
                        const lengthArr = [
                          8, 9, 10, 11, 12, 15, 16, 17, 18, 20,
                        ];
                        const validateLength = lengthArr.includes(value.length);
                        if (
                          validateSpace ||
                          !validateNumberAndChar ||
                          !validateLength
                        ) {
                          return Promise.reject(
                            new Error(
                              t(
                                "请校验纳税人识别号长度是否正确，若包含字母须大写，且不得含有空格",
                              ),
                            ),
                          );
                        }

                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <Input
                    placeholder={
                      titleType === TitleType.Company ? t("必填") : t("选填")
                    }
                    maxLength={64}
                  />
                </Form.Item>
                <More pageType="order" ref={moreRef} />
              </>
            )}
            <Form.Item name="bill_content" label={t("发票内容")}>
              <Select options={billContentOptions} />
            </Form.Item>
            <Form.Item
              name="bill_remark"
              label={t("发票备注")}
              rules={[{ validator: remarkValidator }]}
            >
              <TextArea
                placeholder={t("选填")}
                style={
                  IS_MOBILE
                    ? { height: "auto" }
                    : { height: 120, resize: "none", maxHeight: 120 }
                }
              />
            </Form.Item>
            {/* {billType === 1 && ( */}
            <Form.Item
              label={t("电子邮箱")}
              name="email"
              rules={[
                ({}) => ({
                  validator(_, value) {
                    if (!value) {
                      return Promise.reject(
                        new Error(`${t("电子邮箱")} ${t("不能为空")}`),
                      );
                    }
                    if (
                      /^[a-zA-Z0-9._%+-]{2,}@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(
                        value,
                      )
                    ) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error(t("请输入正确的电子邮箱地址")),
                    );
                  },
                }),
              ]}
            >
              <Input placeholder={t("必填")} autoComplete="off" />
            </Form.Item>
            {additionalParams.orderType === SOURCE_ORDER_TYPE.Meeting && (
              <Form.Item name="print_detail" valuePropName="checked">
                <Checkbox>{t("发票展示单位及数量")} </Checkbox>
              </Form.Item>
            )}
            {/* )} */}
          </Form>
        </>
      </Modal>
      {modalInstance && (
        <LoginModal
          hiddenModal={() => setModalInstance(false)}
          submitAction={submitAction4Login}
        />
      )}
      {attorneyModal && (
        <Modal
          wrapClassName="modal-wrapper attorney-modal-wrapper"
          onCancel={() => setAttorneyModal(false)}
          footer={null}
          width={IS_MOBILE ? "90%" : "420px"}
          title={t("出版社授权书")}
          centered
          maskClosable={false}
          open
        >
          <div className="attorney-wrapper">
            {attorney.map((item, index) => {
              return (
                <div
                  className="attorney-item"
                  key={index}
                  onClick={() =>
                    downloadPowerOfAttorney(item.power_of_attorney)
                  }
                >
                  {t("下载")}&nbsp;
                  {item.publishing_house_name}&nbsp;
                  {t("授权书")}
                </div>
              );
            })}
          </div>
        </Modal>
      )}
    </>
  );
};

export default Invoice;
